/**
 * Canonical color palette — single source of truth for light/dark tokens.
 *
 * EAA-1663: Previously `web-content` sourced its actual Colors palette from a
 * local file (apps/web-content/src/styles/theme.ts) while shared-styles only
 * carried the white-label override mechanism plus an unrelated, incomplete
 * flat `colors` object. That flat object had no consumers outside of theme.ts,
 * so it is superseded here rather than kept alongside this richer set.
 *
 * EAA-1804: the palette now IS the EMG Portal design system's palette (see
 * `portal-tokens.css` for the same values as CSS custom properties, and the
 * "EMG Portal UI" handoff for their provenance). It previously carried a warm
 * / indigo set (`text: #0f172a`, `border: #f3e8e0`, `primary: #6366f1`) that
 * predated the design and disagreed with `design-tokens.css`'s own `:root`.
 * Every screen shifts as a result — that is the intended, approved change, not
 * a side effect: the component sheet specifies its components against these
 * neutrals, so building them on the old ones would produce components that
 * match no design.
 *
 * KEEP IN SYNC WITH `design-tokens.css`. This object feeds Ant Design through
 * `createAntdTheme`; the CSS file feeds every hand-written stylesheet. When the
 * two disagree the app renders in two palettes at once — that is exactly the
 * bug that made the login page dark-text-on-dark-card.
 */

export interface Colors {
    // Primary colors
    primary: string; // Navigation active state, links
    secondary: string; // Hover states, secondary actions
    cta: string; // Primary buttons, highlights
    ctaLight: string; // Light CTA background (e.g., icon backgrounds)
    accentLight: string; // Sidebar selected / soft tint
    accentMedium: string; // Secondary accent / hover ring
    sidebarMenuSelectedBg: string; // Sidebar active row background
    sidebarMenuSelectedText: string; // Sidebar active row + icon color

    // Background colors
    background: string; // Page background
    surface: string; // Cards, modals, table rows
    surfaceHover: string; // Hover state for surface elements
    surfaceElevated: string; // Elevated surfaces (dropdowns, modals)
    // EAA-1804: the sheet's "fill" — inset surfaces, Kbd chips, disabled
    // controls, topbar search. Distinct from `surfaceHover`, which is a
    // transient state; this one is a resting surface.
    fill: string;

    // Text colors — four tiers, matching the sheet's ramp exactly. Three was
    // one too few: the sheet distinguishes label text (#52606D) from metadata
    // (#64707D), and collapsing them lost the difference on every form.
    text: string; // Primary text
    textSecondary: string; // Labels, secondary copy
    textMuted: string; // Metadata, hints
    textLight: string; // Placeholder, disabled
    textOnCta: string; // Text on CTA background

    // Table header
    // EAA-1804: its own token pair. It used to be `cta` + `textOnCta`, which
    // made the header a solid brand bar and coupled two unrelated decisions —
    // "what is our action color" and "how loud is a table header".
    tableHeaderBg: string;
    tableHeaderText: string;

    // Border & divider
    border: string; // Hairline — control borders, table outer
    divider: string; // Lighter rule used inside cards and menus
    // EAA-1804: the ring an unchecked checkbox/radio and a hovered secondary
    // button take. One step darker than `border`, and the sheet uses it often
    // enough that deriving it per call site drifted.
    controlRing: string;

    // Status colors — each is a triple (accent / text-on-tint / tint), because
    // the sheet's status text is never the accent itself: #10B981 on #D1FAE5
    // fails contrast, #047857 passes.
    success: string;
    successLight: string;
    successText: string;
    warning: string;
    warningBg: string;
    warningText: string;
    error: string;
    errorBg: string;
    errorText: string;
    info: string;
    infoBg: string;
    infoBorder: string;

    // Role chips — a ramp of their own, deliberately not the status pills'.
    // A chip sits inside a table cell and must not read as a banner.
    chipOkBg: string;
    chipOkRing: string;
    chipOkText: string;
    chipStaleBg: string;
    chipStaleRing: string;
    chipStaleText: string;

    // Code/Slug background
    codeBg: string; // Background for code/slug display

    // Overlay
    // EAA-1663: added while auditing color usage repo-wide — this exact
    // translucent scrim was independently hardcoded as 'rgba(255, 255, 255,
    // 0.9)' in 4+ files with no shared token backing it.
    overlayScrim: string; // Translucent scrim for loading/refresh overlays
}

/**
 * Light theme colors — canonical base palette (EMG Portal design system).
 */
export const lightColors: Colors = {
    // Primary colors
    primary: '#007FA3',
    secondary: '#3D9CB8',
    cta: '#007FA3',
    ctaLight: '#E6F7FA',
    accentLight: '#F4F5F7',
    accentMedium: '#E5E7EB',
    sidebarMenuSelectedBg: '#E6F7FA',
    sidebarMenuSelectedText: '#006888',

    // Background colors
    background: '#FAFAFA',
    surface: '#FFFFFF',
    surfaceHover: '#FAFAFA',
    surfaceElevated: '#FFFFFF',
    fill: '#F4F5F7',

    // Text colors
    text: '#1F2933',
    textSecondary: '#52606D',
    textMuted: '#64707D',
    textLight: '#94A1AD',
    textOnCta: '#FFFFFF',

    // Table header
    tableHeaderBg: '#FAFAFA',
    tableHeaderText: '#52606D',

    // Border & divider
    border: '#E5E7EB',
    divider: '#ECECF0',
    controlRing: '#CBD2D9',

    // Status colors
    success: '#10B981',
    successLight: '#D1FAE5',
    successText: '#047857',
    warning: '#F59E0B',
    warningBg: '#FFFBEB',
    warningText: '#B45309',
    error: '#D4183D',
    errorBg: '#FEE2E2',
    errorText: '#B91C1C',
    info: '#1D4ED8',
    infoBg: '#DBEAFE',
    infoBorder: '#BFDBFE',

    // Role chips
    chipOkBg: '#F0FDF4',
    chipOkRing: '#BBF7D0',
    chipOkText: '#166534',
    chipStaleBg: '#FEF2F2',
    chipStaleRing: '#FECACA',
    chipStaleText: '#B91C1C',

    // Code/Slug background
    codeBg: '#F4F5F7',

    // Overlay
    overlayScrim: 'rgba(255, 255, 255, 0.9)',
};

/**
 * Dark theme colors.
 *
 * The handoff specifies a dark palette for the page/surface/text/primary ramp
 * but not for every status tint or the chip ramps. Those are derived here in
 * the same spirit (deep tint, light text) rather than left at their light
 * values, which would be unreadable on `#0F1419`.
 */
export const darkColors: Colors = {
    // Primary colors
    primary: '#35A8C6',
    secondary: '#2A8BA5',
    cta: '#35A8C6',
    ctaLight: '#06222B',
    accentLight: '#1D252D',
    accentMedium: '#2A343D',
    sidebarMenuSelectedBg: 'rgba(53, 168, 198, 0.16)',
    sidebarMenuSelectedText: '#35A8C6',

    // Background colors
    background: '#0F1419',
    surface: '#161D24',
    surfaceHover: '#1D252D',
    surfaceElevated: '#1D252D',
    fill: '#1D252D',

    // Text colors
    text: '#E6ECF2',
    textSecondary: '#A8B6C4',
    textMuted: '#A8B6C4',
    textLight: '#7D8B99',
    // EAA-1804: dark `cta` is a light cyan, so text on it must be dark — white
    // here was unreadable (the bug this token was split out to fix).
    textOnCta: '#06222B',

    // Table header
    tableHeaderBg: '#1D252D',
    tableHeaderText: '#A8B6C4',

    // Border & divider
    border: '#2A343D',
    divider: '#222B33',
    controlRing: '#3D4A55',

    // Status colors
    success: '#6EE7B7',
    successLight: '#053E2E',
    successText: '#6EE7B7',
    warning: '#FBBF24',
    warningBg: '#452C05',
    warningText: '#FCD34D',
    error: '#F87171',
    errorBg: '#4C1319',
    errorText: '#FFB3C1',
    info: '#60A5FA',
    infoBg: '#152A52',
    infoBorder: '#1D4ED8',

    // Role chips
    chipOkBg: '#052E16',
    chipOkRing: '#166534',
    chipOkText: '#86EFAC',
    chipStaleBg: '#3F1114',
    chipStaleRing: '#7F1D1D',
    chipStaleText: '#FCA5A5',

    // Code/Slug background
    codeBg: '#1D252D',

    // Overlay
    overlayScrim: 'rgba(15, 20, 25, 0.9)',
};

/**
 * Get colors for a specific theme mode
 */
export const getColors = (isDark: boolean): Colors => {
    return isDark ? darkColors : lightColors;
};
